/**
 * Phone Formatting Utilities
 * Handles parsing and formatting phone numbers from various input formats
 * Consolidated from components/contact/phone.ts and utils/phone.ts
 */

type ContactInput = string | null | undefined

interface PhoneDisplay {
    formatted: string
    copyValue: string
}

/** Format phone digits into display format (XXX) XXX-XXXX */
const formatDigits = (digits: string, extension?: string, fallback?: string): PhoneDisplay | null => {
    if (!digits) {
        return fallback
            ? {
                  formatted: fallback,
                  copyValue: fallback,
              }
            : null
    }

    let normalized = digits

    if (normalized.length === 11 && normalized.startsWith('1')) {
        normalized = normalized.slice(1)
    } else if (normalized.length > 11) {
        normalized = normalized.slice(-10)
    }

    if (normalized.length === 10) {
        const area = normalized.slice(0, 3)
        const prefix = normalized.slice(3, 6)
        const line = normalized.slice(6)
        let formatted = `(${area}) ${prefix}-${line}`
        if (extension) {
            formatted += ` x${extension}`
        }
        return {
            formatted,
            copyValue: formatted,
        }
    }

    const display = extension ? `${digits} x${extension}` : digits || fallback
    if (!display) {
        return null
    }

    return {
        formatted: display,
        copyValue: display,
    }
}

/** Parse phone number from JSON string format */
const parseJsonPhone = (value: string): { number?: string; extension?: string } | null => {
    try {
        const obj = JSON.parse(value) as Record<string, string | null | undefined>
        const number = obj.number || obj.phone || obj.value
        const extension = obj.extension || obj.ext
        return number ? { number, extension: extension || undefined } : null
    } catch {
        return null
    }
}

/** Parse phone number from tel: URI format */
const parseTelPhone = (value: string): { number?: string; extension?: string } | null => {
    const match = /^tel:\+?([\d]+)(?:;ext=([\d]+))?$/i.exec(value.trim())
    if (!match) return null
    return {
        number: match[1],
        extension: match[2],
    }
}

/** Get formatted phone number from various input formats (JSON, tel: URI, plain text) */
export const getFormattedPhone = (input: ContactInput): PhoneDisplay | null => {
    if (!input) return null
    const trimmed = input.trim()
    if (!trimmed) return null

    if (trimmed.startsWith('{')) {
        const parsed = parseJsonPhone(trimmed)
        if (parsed?.number) {
            return formatDigits(parsed.number.replace(/\D/g, ''), parsed.extension || undefined, trimmed)
        }
    }

    if (trimmed.toLowerCase().startsWith('tel:')) {
        const parsed = parseTelPhone(trimmed)
        if (parsed?.number) {
            return formatDigits(parsed.number.replace(/\D/g, ''), parsed.extension || undefined, trimmed)
        }
    }

    // Plain text — may carry a trailing extension as "x17", "ext 17" or "ext.17". DAT (and the
    // extension's extractPhone) format phones as "+1 8159165667 x17", so split the extension off
    // FIRST: otherwise stripping all non-digits folds its digits onto the number ("1815916566717"),
    // and formatDigits' slice(-10) then yields a wrong number ("(591) 656-6717") and drops the ext.
    const extMatch = /(?:ext\.?|x)\s*(\d+)\s*$/i.exec(trimmed)
    const numberPart = extMatch ? trimmed.slice(0, extMatch.index) : trimmed
    const extension = extMatch?.[1]
    const digits = numberPart.replace(/\D/g, '')

    return formatDigits(digits, extension, trimmed)
}
